/** @jsxImportSource @emotion/react */
import React, { memo, useCallback, useMemo, useState } from 'react'
import Menu from '@material-ui/core/Menu'
import Slide from '@material-ui/core/Slide'
import useSWR from 'swr'
import getAPIDomain from '@signal/utils/env/apiDomain'
import { getChangeTenantUrl } from '@signal/core/auth/auth'
import { fetchData } from '@signal/core/swr/fetch'
import withCommonSnapshotsOnly from '@signal/hoc/withCommonSnapshotsOnly'
import { BrandingWrapper, PureCarsBrand } from '../CommonPage/styles'
import { CustomMenuItem } from './styles'
import { TenantThemeType } from '@signal/core/tenantTheme/types'

const COMPACT_LOGO_PROPS = {
  height: 60,
  width: 40
}

export type TenantType = {
  id: number
  name: string
  theme?: string
}

export type TenantStatus = {
  currentTenant: TenantType
  availableTenants: TenantType[]
}

export type TenantMenuProps = {
  compact?: boolean
}

function TenantMenu({ compact = false }: TenantMenuProps) {
  const tenantUrl = `${getAPIDomain('/sheriff/authorize')}/v1/tenants/current`
  const { data } = useSWR<TenantStatus>(tenantUrl, url => fetchData({ url }))
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleOpen = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if ((data?.availableTenants?.length ?? 0) > 1) {
        setAnchorEl(e.currentTarget)
      }
    },
    [data]
  )

  const handleClose = useCallback(() => setAnchorEl(null), [])

  const handleSelect = useCallback(
    (tenantId: number) => () => {
      if (!data?.currentTenant || tenantId === data.currentTenant.id) {
        setAnchorEl(null)
        return
      }
      window.location.assign(getChangeTenantUrl(tenantId))
    },
    [data]
  )

  // Memoize a single sorted tenant list with deserialized theme attached
  const sortedTenantsWithTheme = useMemo(() => {
    if (!data) return []
    const currentId = data.currentTenant?.id
    return [...data.availableTenants]
      .map(t => ({
        ...t,
        parsedTheme: t.theme
          ? (() => {
              try {
                return JSON.parse(t.theme) as TenantThemeType
              } catch {
                return undefined
              }
            })()
          : undefined
      }))
      .sort((a, b) => {
        if (a.id === currentId) return -1
        if (b.id === currentId) return 1
        return 0
      })
  }, [data])

  const currentTenant = sortedTenantsWithTheme[0]
  const currentLogo = currentTenant?.parsedTheme?.tenantLogo
  const currentThemeColor = currentTenant?.parsedTheme?.palette.primary1Color

  const brandingStyle: React.CSSProperties | undefined = compact
    ? { justifyContent: 'center' }
    : undefined
  const logoProps = compact ? COMPACT_LOGO_PROPS : {}

  const hasMultiple = (data?.availableTenants?.length ?? 0) > 1
  if (!hasMultiple) {
    return (
      <div>
        <BrandingWrapper style={brandingStyle}>
          {currentLogo ? (
            <PureCarsBrand src={currentLogo} {...logoProps} />
          ) : (
            <PureCarsBrand {...logoProps} />
          )}
        </BrandingWrapper>
      </div>
    )
  }

  return (
    <>
      <div
        onClick={handleOpen}
        style={
          compact ? { cursor: 'pointer', width: '100%' } : { cursor: 'pointer' }
        }
      >
        <BrandingWrapper
          selectedColor={currentThemeColor}
          clickable={true}
          className={compact ? undefined : 'header-branding'}
          style={brandingStyle}
        >
          {currentLogo ? (
            <PureCarsBrand src={currentLogo} {...logoProps} />
          ) : (
            <PureCarsBrand {...logoProps} />
          )}
        </BrandingWrapper>
      </div>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ style: { marginTop: '-32px' } }}
        TransitionComponent={Slide}
        // @ts-ignore
        TransitionProps={{ direction: 'down', timeout: 400 }}
      >
        {sortedTenantsWithTheme.map(t => {
          const theme = t.parsedTheme
          const logo = theme?.tenantLogo || currentLogo
          const colors = theme?.palette
          const selectedColor = colors?.primary1Color || currentThemeColor
          return (
            <CustomMenuItem
              key={t.id}
              onClick={handleSelect(t.id)}
              selected={t.id === currentTenant.id}
            >
              <BrandingWrapper
                clickable={true}
                selected={t.id === currentTenant.id}
                selectedColor={selectedColor}
              >
                {logo ? (
                  <PureCarsBrand src={logo} height={60} width={147} />
                ) : (
                  <PureCarsBrand height={60} width={147} />
                )}
                <span
                  style={{
                    marginLeft: 0,
                    marginRight: 16,
                    fontSize: 16,
                    fontWeight: 600
                  }}
                >
                  {t.name}
                </span>
              </BrandingWrapper>
            </CustomMenuItem>
          )
        })}
      </Menu>
    </>
  )
}

export default memo(withCommonSnapshotsOnly(TenantMenu))
